import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { api, post } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { fmtDayMonth } from '@siva/shared';
import { Alert, fmtDate, Spinner } from '../lib/ui.js';
import { Card } from '../lib/components.js';
import { t } from '../i18n/index.js';

type AttentionItem = {
  id: string;
  tone: 'red' | 'amber';
  title: string;
  detail: string;
  action?: {
    label: string;
    href?: string;
    post?: string;
    body?: Record<string, unknown>;
    done?: string;
  };
};

/** An amount in both currencies; null where no exchange rate was available. */
type Money = { usd_cents: number | null; inr_paise: number | null };
type DailyCost = { date: string; amount: Money };
type Costs =
  | { state: 'not_configured' }
  | {
      state: 'ok' | 'stale';
      month: string;
      /** What the account is billed in: those figures are exact, the other is converted. */
      currency: 'USD' | 'INR';
      current: Money;
      previous: Money;
      budget: Money;
      breakdown: ({ name: string } & Money)[];
      /** This month day by day, for the graph. */
      daily: DailyCost[];
      rate: { usd_inr: number; date: string; source: 'ecb' | 'fixed' } | null;
      fetched_at: string;
      error: string | null;
    };

type Overview = {
  attention: AttentionItem[];
  costs: Costs;
  activity: { punches_24h: number };
  email_configured: boolean;
  backups_configured: boolean;
};

const usd = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inr = (paise: number) => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
/** The exact figure first, the converted one after it with "≈"; one alone when no rate. */
function both(m: Money, exact: 'USD' | 'INR'): string {
  const d = m.usd_cents === null ? null : usd(m.usd_cents);
  const r = m.inr_paise === null ? null : inr(m.inr_paise);
  const [first, second] = exact === 'INR' ? [r, d] : [d, r];
  return [first, second && `≈ ${second}`].filter(Boolean).join(' · ') || '—';
}

/**
 * The top of the Admin page: whether anything needs the owner, and what the month is costing.
 * Deliberately free of server vocabulary — anything technical lives behind the expander below.
 */
export function AdminOverview({ onRefresh }: { onRefresh: () => void }) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<Overview>('/admin/overview'));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (item: AttentionItem) => {
    if (!item.action?.post) return;
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      await post(item.action.post, item.action.body ?? {});
      setNote(item.action.done ?? t('admin_backup_requested'));
      await load();
      onRefresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <Alert>{error}</Alert>;
  if (!data) return <Spinner />;

  const n = data.attention.length;
  const worst = data.attention.some((i) => i.tone === 'red') ? 'red' : 'amber';

  return (
    <>
      {error && <Alert>{error}</Alert>}
      {note && <p className="muted">{note}</p>}

      <Card className={`status-line ${n === 0 ? 'all-good' : worst}`}>
        <p className="status-headline">
          {n === 0 ? (
            <>
              <CircleCheck size={22} aria-hidden="true" /> {t('admin_all_good')}
            </>
          ) : (
            <>
              <TriangleAlert size={22} aria-hidden="true" />{' '}
              {n === 1 ? t('admin_one_thing') : t('admin_n_things', { n })}
            </>
          )}
        </p>
        <p className="muted">
          {t('admin_activity', { n: data.activity.punches_24h })}
        </p>
      </Card>

      {n > 0 && (
        <Card title={t('admin_attention')}>
          <ul className="list attention">
            {data.attention.map((item) => (
              <li key={item.id} className={`attention-item ${item.tone}`}>
                <div>
                  <strong>{item.title}</strong>
                  <div className="muted">{item.detail}</div>
                </div>
                {item.action &&
                  (item.action.href ? (
                    <Link className="button small secondary" to={item.action.href}>
                      {item.action.label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="small secondary"
                      disabled={busy}
                      onClick={() => void act(item)}
                    >
                      {item.action.label}
                    </button>
                  ))}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <CostsCard costs={data.costs} />

      {!data.email_configured && (
        <Card id="email-setup" title={t('admin_email_setup')}>
          <p>{t('admin_email_setup_body')}</p>
          <ol className="a2hs-steps">
            <li>{t('admin_email_step_1')}</li>
            <li>{t('admin_email_step_2')}</li>
            <li>{t('admin_email_step_3')}</li>
          </ol>
        </Card>
      )}
    </>
  );
}

/**
 * This month day by day.
 *
 * The shape answers "is something running that should not be", and for a while that was all it
 * offered: no scale, no dates, and each day's figure on hover — which a phone does not have, and
 * the phone is where this gets read. So the graph now says what it is made of. The dashed line
 * across the top is the busiest day and carries its figure, so the bars have a scale. The row
 * underneath names the first and last day drawn. And every bar is a button: tapping one puts that
 * date and what it cost beside the heading, which is the whole of the reading on a touch screen.
 *
 * It opens on the most recent day, because "what did yesterday cost" is the question being asked
 * most of the time. The tallest day still sets the scale, so a month costing pennies shows its
 * shape rather than a flat line.
 */
function CostGraph({ days, exact }: { days: DailyCost[]; exact: 'USD' | 'INR' }) {
  const value = (d: DailyCost) => (exact === 'INR' ? d.amount.inr_paise : d.amount.usd_cents) ?? 0;
  const [picked, setPicked] = useState<string | null>(null);
  const top = Math.max(0, ...days.map(value));
  // Never divide by zero, and never let a month with no spend draw bars out of rounding noise.
  const scale = Math.max(1, top);
  const first = days[0];
  const last = days[days.length - 1];
  const shown = days.find((d) => d.date === picked) ?? last;
  if (!first || !last || !shown) return null;
  return (
    <figure className="cost-graph-fig">
      <figcaption className="cost-graph-head">
        <span className="muted">{t('admin_costs_graph_title')}</span>
        <span className="cost-graph-read">
          {fmtDayMonth(shown.date)} · {both(shown.amount, exact)}
        </span>
      </figcaption>
      <div className="cost-graph-frame">
        {top > 0 && (
          <span className="cost-graph-peak">
            {t('admin_costs_graph_peak', { amount: exact === 'INR' ? inr(top) : usd(top) })}
          </span>
        )}
        <div className="cost-graph" role="group" aria-label={t('admin_costs_graph_alt')}>
          {days.map((d) => {
            const label = `${fmtDayMonth(d.date)} · ${both(d.amount, exact)}`;
            return (
              <button
                type="button"
                key={d.date}
                className={`cost-graph-col${d.date === shown.date ? ' picked' : ''}`}
                onClick={() => setPicked(d.date)}
                aria-label={label}
                title={label}
              >
                <span
                  className={`cost-graph-bar${value(d) === 0 ? ' zero' : ''}`}
                  style={{ height: `${Math.max(2, Math.round((value(d) / scale) * 100))}%` }}
                />
              </button>
            );
          })}
        </div>
      </div>
      <p className="cost-graph-axis">
        <span>{fmtDayMonth(first.date)}</span>
        <span className="cost-graph-hint">{t('admin_costs_graph_hint')}</span>
        <span>{fmtDayMonth(last.date)}</span>
      </p>
    </figure>
  );
}

function CostsCard({ costs: summary }: { costs: Costs }) {
  /**
   * Nothing is asked of Amazon until this is opened.
   *
   * The page arrives with whatever the daily job last stored, which costs nothing to read and is
   * what the budget warning is raised from. Opening the panel asks for a fresh figure — one
   * request, one cent, and only if what is stored has gone stale.
   */
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Costs | null>(null);
  const [busy, setBusy] = useState(false);

  const expand = async () => {
    setOpen(true);
    if (detail || busy) return;
    setBusy(true);
    try {
      const r = await api<{ costs: Costs }>('/admin/costs');
      setDetail(r.costs);
    } catch {
      // The stored figures are still shown; a failed refresh is not a reason to show nothing.
    } finally {
      setBusy(false);
    }
  };

  const costs = detail ?? summary;
  if (!open) {
    return (
      <Card id="costs" title={t('admin_costs')}>
        <p className="muted">{t('admin_costs_collapsed')}</p>
        <button type="button" className="secondary" onClick={() => void expand()}>
          {t('admin_costs_view')}
        </button>
      </Card>
    );
  }
  return <CostsPanel costs={costs} busy={busy} />;
}

function CostsPanel({ costs, busy }: { costs: Costs; busy: boolean }) {
  if (costs.state === 'not_configured') {
    return (
      <Card id="costs" title={t('admin_costs')}>
        <p className="muted">{t('admin_costs_not_configured')}</p>
      </Card>
    );
  }
  const exact = costs.currency;
  const minor = (m: Money) => (exact === 'INR' ? m.inr_paise : m.usd_cents);
  const budget = minor(costs.budget) ?? 0;
  const spent = minor(costs.current);
  const pct = budget > 0 && spent !== null ? Math.min(100, Math.round((spent / budget) * 100)) : null;
  const over = budget > 0 && spent !== null && spent > budget;
  // The big figure is the exact one — dollars, or rupees for an account billed in rupees —
  // and the conversion sits beside it.
  const big = exact === 'INR' ? costs.current.inr_paise : costs.current.usd_cents;
  const small = exact === 'INR' ? costs.current.usd_cents : costs.current.inr_paise;

  return (
    <Card
      id="costs"
      title={t('admin_costs')}
      subtitle={t('admin_costs_as_of', { time: fmtDate(costs.fetched_at, true) })}
    >
      {busy && <p className="muted">{t('admin_costs_refreshing')}</p>}
      {costs.error && <p className="muted bad">{t('admin_costs_stale', { why: costs.error })}</p>}
      <p className="cost-figure">
        <strong>{big === null ? '—' : exact === 'INR' ? inr(big) : usd(big)}</strong>
        {small !== null && (
          <span className="cost-alt">≈ {exact === 'INR' ? usd(small) : inr(small)}</span>
        )}{' '}
        <span className="muted">{t('admin_costs_this_month')}</span>
      </p>
      {budget > 0 && pct !== null && (
        <>
          <div className="cost-bar" role="img" aria-label={t('admin_costs_bar_alt')}>
            <span className={over ? 'over' : ''} style={{ width: `${pct}%` }} />
          </div>
          <p className="muted">
            {t('admin_costs_budget', {
              used: both(costs.current, exact),
              budget: both(costs.budget, exact),
            })}
          </p>
        </>
      )}
      {costs.daily.length > 0 && <CostGraph days={costs.daily} exact={exact} />}
      <p className="muted">{t('admin_costs_last_month', { amount: both(costs.previous, exact) })}</p>
      {costs.breakdown.length > 0 && (
        <dl className="cost-split">
          {costs.breakdown.map((b) => (
            <div key={b.name}>
              <dt>{b.name}</dt>
              <dd>{both(b, exact)}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}
